import { createContext, createElement, useContext, useEffect, useState, type PropsWithChildren } from "react";
import { api } from "../api/client";
import type { FinanceCapabilities } from "../api/types";

const NO_FINANCE_ACCESS: FinanceCapabilities = {
  canRequestMoney: false,
  canConfirmMoney: false,
  canManageBankLedger: false,
  canViewPayouts: false,
  canExecuteBank: false,
};

const FinanceCapabilitiesOverrideContext = createContext<FinanceCapabilities | undefined>(undefined);

export function FinanceCapabilitiesOverrideProvider({
  capabilities,
  children,
}: PropsWithChildren<{ capabilities?: FinanceCapabilities }>) {
  return createElement(FinanceCapabilitiesOverrideContext.Provider, { value: capabilities }, children);
}

export function useFinanceCapabilities(options: { disabled?: boolean } = {}) {
  const override = useContext(FinanceCapabilitiesOverrideContext);
  const [capabilities, setCapabilities] = useState<FinanceCapabilities>(NO_FINANCE_ACCESS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (override) {
      setCapabilities(override);
      setLoading(false);
      return;
    }
    if (options.disabled) {
      setCapabilities(NO_FINANCE_ACCESS);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    api.financeCapabilities(controller.signal)
      .then(setCapabilities)
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setCapabilities(NO_FINANCE_ACCESS);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [options.disabled, override]);

  return override ? { capabilities: override, loading: false } : { capabilities, loading };
}
